import React, { useEffect, useState, useRef } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';

interface QuestConfettiProps {
  type: 'progress' | 'final';
  isActive: boolean;
  onComplete?: () => void;
}

export const QuestConfetti: React.FC<QuestConfettiProps> = ({ 
  type, 
  isActive,
  onComplete 
}) => {
  const [animationData, setAnimationData] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  // Load animation data when component mounts
  useEffect(() => {
    if (isActive && !animationData) {
      const animationPath = '/lotti/Confetti.json'; // Use new Confetti.json for all
      
      console.log(`[QuestConfetti] Loading ${type} animation from:`, animationPath);
      fetch(animationPath)
        .then(response => response.json())
        .then(data => {
          console.log(`[QuestConfetti] Successfully loaded ${type} animation:`, data);
          setAnimationData(data);
          setIsLoaded(true);
        })
        .catch(error => {
          console.error(`[QuestConfetti] Failed to load ${type} animation:`, error);
        });
    }
  }, [isActive, type, animationData]);

  // Control animation speed - start fast, then slow down
  useEffect(() => {
    if (isActive && isLoaded && lottieRef.current) {
      // Start with faster speed
      lottieRef.current.setSpeed(2.5);
      
      // Gradually slow down the animation
      const slowDownInterval = setInterval(() => {
        if (lottieRef.current) {
          const currentSpeed = lottieRef.current.animationItem?.playSpeed || 2.5;
          if (currentSpeed > 0.8) {
            lottieRef.current.setSpeed(currentSpeed * 0.85); // Exponential slowdown
          }
        }
      }, 200);

      return () => clearInterval(slowDownInterval);
    }
  }, [isActive, isLoaded]);

  // Auto cleanup after animation duration
  useEffect(() => {
    if (isActive && isLoaded) {
      const duration = 10000; // Match Lottie animation duration (10 seconds)
      const timer = setTimeout(() => {
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isActive, isLoaded, type, onComplete]);

  if (!isActive || !animationData) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ 
          duration: 0.3,
          ease: [0.34, 1.56, 0.64, 1] // Custom ease for bounce effect
        }}
        className="fixed left-0 right-0 pointer-events-none overflow-visible"
        style={{ 
          top: 0, // Start from top of screen
          bottom: '5rem', // End at quest box (bottom-20 = 5rem from bottom)
          zIndex: -1, // Behind the card content
        }}
      >
        <div className="w-full h-full">
          <Lottie
            lottieRef={lottieRef}
            animationData={animationData}
            loop={false}
            autoplay={true}
            style={{
              width: '100%', // Full width
              height: '100%', // Full height of available space
            }}
            rendererSettings={{
              preserveAspectRatio: 'xMidYMid slice' // Slice to fill the space
            }}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};